<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import {
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue'
import TtButton from './TtButton.vue'
import TtDatepicker from './TtDatepicker.vue'
import {
  startOfToday,
  endOfDay,
  startOfDay,
  differenceInDays,
  differenceInCalendarDays,
  differenceInHours,
  isWithinInterval,
  isBefore,
  isToday,
  isSameDay,
  startOfYesterday
} from 'date-fns'
import cloneDeep from 'lodash/cloneDeep'
import { useI18n } from 'vue-i18n'
import { useLocationStore } from '@/store/location'
import { useProfileStore } from '@/store/profile'
import type {
  Location,
  Parking,
  ParkingBooking,
  BookingConfirmation,
  MetaValue
} from '@/location'
import type { User } from '@/account'
import TtInput from '@/components/TtInput.vue'
import TtContainer from './TtContainer.vue'
import IconParking from './Icon/ParkingPaid.vue'
import IconCard from './Icon/Card.vue'
import IconWallet from './Icon/Wallet.vue'
import IconClose from './Icon/Close.vue'
import TtParkingTile from './TtParkingTile.vue'
import ProfilePicture from './Icon/ProfilePicture.vue'
import ParkingBookingConfirmation from './ParkingBookingConfirmation.vue'
import { onMounted } from 'vue'
import axios from '@/axios'
import { useAccountStore } from '@/store/account'
import { useNotificationsStore } from '@/store/notifications'
import { useRoute, useRouter } from 'vue-router'
import { isNative } from '@/helpers'
import { convertCurrency } from '@/utils/currency'

const locations = useLocationStore()
const notificationsStore = useNotificationsStore()
const { currentCompany, currentVehicle } = useProfileStore()
const { user, hasRole } = useAccountStore()
const { t } = useI18n({ useScope: 'global' })
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  location: Location
  parking: Parking
}>()

const modalOpen = ref(false)
const confirmationModalOpen = ref(false)
const fromDate = ref<Date | undefined>(undefined)
const toDate = ref<Date | undefined>(undefined)
const regNumber = ref(currentVehicle?.reg_number ?? '')
const waitingPayment = ref(false)
const bookingObjectForConfirmation = ref<BookingConfirmation>()

const bookings = ref<ParkingBooking[]>([])
onMounted(async () => {
  const fetchResponse = await locations.fetchParkingBookings(props.parking.id)
  if (fetchResponse.error) {
    return
  }
  bookings.value = fetchResponse
})

const openhours = computed(() => {
  const dayNames = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ]
  const meta = props.location.meta.find((m) => m.type == 'openhours')
  if ((meta?.value as any).key) {
    let metaData = (meta?.value as any).key as string
    metaData = metaData
      .replaceAll('"open"', '"opens"')
      .replaceAll('"close"', '"closes"')
    const oh = JSON.parse(metaData)
    if (Object.keys(oh).includes('allweek')) {
      if (oh.allweek.allday == true) {
        return {}
      } else if (oh.allweek.opens) {
        const res: any = {}
        dayNames.forEach((name) => {
          res[name] = {
            opens: oh.allweek.opens,
            closes: oh.allweek.closes
          }
        })
        return res
      }
    } else {
      dayNames.forEach((name) => {
        if (oh[name]?.allday) {
          oh[name] = {
            opens: '00:00',
            closes: '23:59'
          }
        }
      })
    }
    return oh
  }
  return {}
})

const booked = computed(() => {
  const today = new Date()
  return bookings.value.some((booking) => {
    if (new Date(booking.from) > today) {
      return false
    }
    if (new Date(booking.to) < today) {
      return false
    }
    return true
  })

  // feature/timpris
  // if (timpris) {
  //   const booking = bookings.value.filter((b) => {
  //       const from = new Date(b.from.replace(/Z.*$/, ''))
  //       const to = new Date(b.to.replace(/Z.*$/, ''))
  //       return isWithinInterval(today, { start: from, end: to })
  //   })
  //   return booking.length > 0
  // }
})

const maxDate = computed(() => {
  // This is to prevent the user from booking a date range containing an already booked date
  if (fromDate.value) {
    const nextBookedDate = bookings.value.find(
      (booking) => new Date(booking.from) > fromDate.value!
    )
    if (nextBookedDate) {
      const from = new Date(nextBookedDate.from)
      from.setDate(from.getDate() + 1)
      return from
    }
  }
  return undefined
})

const availablePaymentOptions = computed(() => {
  const options = []
  if (!currentCompany?.id) {
    options.push('card')
  }
  if (currentCompany?.id) {
    options.push('invoice')
  }
  return options
})

const paymentOption = ref(currentCompany?.id ? 'invoice' : 'card')
const acceptTerms = ref(false)

const truckers = computed(() => {
  return currentCompany?.users ?? ([] as User[])
})
const selectedTrucker = ref(currentCompany?.users?.[0] ?? ({} as User))

const locationImageSrc = computed(() => {
  return `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${
    props.parking.coordinates.lng
  },${props.parking.coordinates.lat},18,0/600x400?access_token=${
    import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  }`
})

const setStartDate = (value: Date) => {
  fromDate.value = value
  if (toDate.value && isBefore(toDate.value, value)) {
    toDate.value = undefined
  }
}

watch(fromDate, (value) => {
  toDate.value = undefined
})

watch(toDate, (value) => {
  // safety
  const booking = bookings.value.filter((b) => {
    const from = new Date(b.from)
    from.setSeconds(from.getSeconds() + 1)
    const to = new Date(b.to)
    //to.setSeconds(to.getSeconds() - 1)
    return (
      value != undefined && isWithinInterval(value, { start: from, end: to })
    )
  })
  if (booking.length > 0) {
    toDate.value = undefined
  }
})

const currentRegNumber = computed(() => {
  return regNumber.value
})

const openExternalLink = async (url: string) => {
  if (Capacitor.getPlatform() === 'web') {
    window.open(url, '_self')
  } else {
    await Browser.open({
      url,
      presentationStyle: 'popover'
    })
  }
}

const bookParking = async () => {
  if (paymentOption.value == 'card') {
    let baseUrl

    switch (Capacitor.getPlatform()) {
      case 'web':
        baseUrl = location.origin
        break
      case 'ios':
        baseUrl = `${import.meta.env.VITE_APP_URL}/app-link/ios`
        break
      case 'android':
        baseUrl = `${import.meta.env.VITE_APP_URL}/app-link/android`
        break
    }

    const url = new URL(
      `${baseUrl}/location/${props.location.id}`,
      location.href
    )
    // url.searchParams.append('parkingId', props.parking.id)
    // if (isNative) {
    //   url.protocol = 'tt:'
    // }
    var response
    try {
      response = await instance.post('/charge/direct/parking', {
        location_id: props.location.id,
        parking_id: props.parking.id,
        from: new Date(fromDate.value ?? '').toISOString(),
        to: new Date(toDate.value ?? '').toISOString(),
        reg_number: currentRegNumber.value,
        return: url
      })
    } catch (error) {
      console.error(error)
    }

    if (response && response.data.url) {
      waitingPayment.value = true
      openExternalLink(response.data.url)
      // window.open(response.data.url, isNative ? '_blank' : '_top')?.focus();
      // window.location.href = response.data.url
      notificationsStore.addNotification(
        'location.waitingStripePayment',
        t('location.waitingStripePayment'),
        'notification',
        true,
        false,
        false,
        true
      )
    } else {
      console.error(response)
      notificationsStore.addNotification(
        'location.paymentError',
        t('location.somethingWrongWithPayment'),
        'notification',
        true,
        false,
        false,
        true
      )
    }
  } else {
    const response = await instance.post('/charge/bill/parking', {
      location_id: props.location.id,
      parking_id: props.parking.id,
      from: new Date(fromDate.value ?? '').toISOString(),
      to: new Date(toDate.value ?? '').toISOString(),
      reg_number: currentRegNumber.value,
      company_id: currentCompany?.id
    })
    if (response.data.success) {
      bookingObjectForConfirmation.value = {
        id: response.data.parkingBookingId,
        from: new Date(response.data.from),
        to: new Date(response.data.to)
      } as BookingConfirmation
      confirmationModalOpen.value = true
      modalOpen.value = false
    } else {
      console.error(response)
    }
  }
}

const validatePayment = async () => {
  const checkoutId = route.query.checkoutId?.toString()
  const parkingBookingId = route.query.parkingBookingId?.toString()
  if (checkoutId) {
    const response = await instance.post('/charge/direct/check', {
      parking_booking_id: parkingBookingId,
      checkout_id: checkoutId
    })
    if (response.data.status == 'paid') {
      waitingPayment.value = false
      notificationsStore.removeNotification('location.waitingStripePayment')
      router.replace({ query: {} })
      bookingObjectForConfirmation.value = {
        id: response.data.parkingBookingId,
        from: new Date(response.data.from),
        to: new Date(response.data.to)
      } as BookingConfirmation
      confirmationModalOpen.value = true
      modalOpen.value = false
      await Browser.close()
    } else {
      console.error(response)
      notificationsStore.addNotification(
        'location.paymentError',
        t('location.somethingWrongWithPayment'),
        'notification',
        true,
        false,
        false,
        true
      )
    }
  }
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_GATEWAY_URL + '/billing'
})

/*
const sendInvoice = async () => {
  const response = await instance.post('/invoice', {
    ParkingId: props.parking.id,
    RegNumber: regNumber.value,
    From: fromDate.value,
    To: toDate.value,
    Mail: user?.email
  })
  console.log(response.data)
}
*/

const datepickerFrom = ref<InstanceType<typeof TtDatepicker> | null>(null)
const datepickerTo = ref<InstanceType<typeof TtDatepicker> | null>(null)

const closeDateFrom = () => {
  datepickerFrom.value && datepickerFrom.value.close()
}
const closeDateTo = () => {
  datepickerTo.value && datepickerTo.value.close()
}

const fromDisabledDates = computed(() => {
  const dates = []
  dates.push({
    start: null,
    end: startOfYesterday()
  })

  // Block non openhours dates
  if (Object.keys(openhours.value).length > 0) {
    const end = new Date()
    end.setFullYear(end.getFullYear() + 1)
    for (var d = new Date(); d <= end; d.setDate(d.getDate() + 1)) {
      const dayName = new Date(d)
        .toLocaleDateString('en-US', { weekday: 'long' })
        .toLowerCase()
      if (!Object.keys(openhours.value).includes(dayName)) {
        dates.push(new Date(d))
      }
    }
  }

  // /* start day price */
  // if (bookings.value.length) {
  //   const fullyBooked = []
  //   bookings.value.sort((a, b) => {
  //     const aDate = new Date(a.from)
  //     const bDate = new Date(b.from)
  //     return aDate.getTime() - bDate.getTime()
  //   })
  //   for (let i = 0; i < bookings.value.length; i++) {
  //     const booking = bookings.value[i]
  //     const from = new Date(booking.from)
  //     const to = new Date(booking.to)
  //     const dayBeforeTo = new Date(to)
  //     dayBeforeTo.setDate(dayBeforeTo.getDate() - 1)
  //     fullyBooked.push({
  //       start: from,
  //       end: dayBeforeTo
  //     })

  //     const nextBookedDate = bookings.value[i + 1]
  //     const nextFrom = nextBookedDate ? new Date(nextBookedDate.from) : null
  //     if (nextFrom) {
  //       // If the next booking is the day after the current booking, and there is less than 24 hours between them, add to fully booked
  //       const daysDiff = differenceInDays(nextFrom, to)
  //       if (daysDiff == 0) {
  //         fullyBooked.push(to)
  //         fullyBooked.push(nextFrom)
  //       }
  //     }
  //   }
  //   dates.push(...fullyBooked)
  // }
  // /* end day price */

  /* start hour price */
  // Get hours left per day
  const fullyBooked: any = {}
  bookings.value.forEach((booking) => {
    const from = new Date(booking.from)
    const to = new Date(booking.to)
    const date = cloneDeep(from)
    const daysDiff = differenceInDays(to, from)
    for (let i = 0; i <= daysDiff; i++) {
      date.setDate(from.getDate() + i)
      const key = date.toLocaleDateString()
      if (i > 0) {
        date.setHours(0, 0, 0, 0)
      }
      let nextDate = cloneDeep(date)
      nextDate.setDate(nextDate.getDate() + 1)
      nextDate = to > startOfDay(nextDate) ? startOfDay(nextDate) : to
      let value = differenceInHours(nextDate, date)
      if (
        value > 24 ||
        (fullyBooked[key] && fullyBooked[key].hours + value > 24)
      ) {
        value = 24
      }
      if (fullyBooked[key]) {
        fullyBooked[key].hours = value
      } else {
        fullyBooked[key] = {
          date: cloneDeep(date),
          hours: value
        }
      }
    }
  })
  Object.values(fullyBooked)
    .filter((fb: any) => fb.hours >= 24)
    .forEach((fb: any) => {
      dates.push({ start: fb.date, end: fb.date })
    })

  // No slots today
  let hit = false
  for (let i = 0; i <= 24; i++) {
    if (fromDateRules.value.hours(i, { date: new Date() })) {
      hit = true
    }
  }
  if (!hit) {
    dates.push(new Date())
  }
  /* end hour price */

  if (props.parking.id == 'c0988906-2309-49af-b287-aac0b15b7f4a')
    console.log('fromDisabledDates', dates)

  return dates
})
const fromDateRules = ref({
  hours: (hour: number, { date }: { date: Date }) => {
    if (!date) return false
    // /* start day price */
    // // Messy, needs cleaning.
    // //Checks if there is a check in/check out on the selected day, if so, only allow hours before/after the check in/check out
    // //Also checks if the next day has a booking, and if so, only allow hours >24 hours before the check in
    // const fromBooking = bookings.value.find((booking) => {
    //   const bookingDate = new Date(booking.from)
    //   return isSameDay(bookingDate, date)
    // })
    // const toBooking = bookings.value.find((booking) => {
    //   const bookingDate = new Date(booking.to)
    //   return isSameDay(bookingDate, date)
    // })

    // const nextDayBooking = bookings.value.find((booking) => {
    //   const bookingDate = new Date(booking.from)
    //   const nextDay = new Date(date)
    //   nextDay.setDate(nextDay.getDate() + 1)
    //   return isSameDay(bookingDate, nextDay)
    // })

    // let hourAllowed = true
    // if (isToday(date)) {
    //   const now = new Date()
    //   hourAllowed = now.getHours() <= hour
    //   if (!hourAllowed) return false
    // }

    // if (nextDayBooking) {
    //   const nextDayBookingDate = new Date(nextDayBooking.from)
    //   hourAllowed = nextDayBookingDate.getHours() >= hour
    //   if (!hourAllowed) return false
    // }

    // if (fromBooking) {
    //   const from = new Date(fromBooking.from)
    //   hourAllowed = from.getHours() >= hour
    // } else if (toBooking) {
    //   const to = new Date(toBooking.to)
    //   hourAllowed = to.getHours() <= hour
    // }

    // return hourAllowed
    // /* end day price */

    // Block non openhours dates
    let isOpen = true
    const dayName = date
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase()
    if (Object.keys(openhours.value).includes(dayName)) {
      const opens = parseInt(openhours.value[dayName].opens.split(':')[0])
      let close = openhours.value[dayName].closes
      if (close == '23:59' || close == '00:00') {
        close = '24:00'
      }
      const closes = parseInt(close.split(':')[0])
      isOpen = hour >= opens && hour < closes
    }

    /* start hour price */
    const booking = bookings.value.filter((b) => {
      const from = new Date(b.from)
      const to = new Date(b.to)

      if (isSameDay(from, date) && isSameDay(to, date)) {
        return hour >= from.getHours() && hour < to.getHours()
      } else if (isSameDay(from, date)) {
        return hour >= from.getHours()
      } else if (isSameDay(to, date)) {
        return hour <= to.getHours()
      } else if (isWithinInterval(date, { start: from, end: to })) {
        return true
      }
      return false
    })
    return (
      booking.length == 0 &&
      !(isToday(date) && new Date().getHours() >= hour) &&
      isOpen
    )
    /* end hour price */
  },
  minutes: 0
})

const toDisabledDates = computed(() => {
  const from = cloneDeep(fromDate.value || startOfToday())
  const dayBeforeTo = cloneDeep(from)
  dayBeforeTo.setDate(dayBeforeTo.getDate() - 1)
  const dates = []
  dates.push({
    start: null,
    end: dayBeforeTo
  })

  // Block non openhours dates
  // if (Object.keys(openhours.value).length > 0) {
  //   const end = new Date()
  //   end.setFullYear(end.getFullYear() + 1)
  //   for (var d = cloneDeep(from); d <= end; d.setDate(d.getDate() + 1)) {
  //     const dayName = new Date(d).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
  //     if (!Object.keys(openhours.value).includes(dayName)) {
  //       dates.push(new Date(d))
  //       const blockFromDate = new Date(d)
  //       blockFromDate.setDate(blockFromDate.getDate() + 1)
  //       dates.push({
  //         start: blockFromDate,
  //         end
  //       })
  //       break
  //     }
  //   }
  // }
  // const dayName = new Date(from).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
  // if (Object.keys(openhours.value).includes(dayName)) {
  //   if (!(['00:00', '23:59', '24:00'].includes(openhours.value[dayName].closes) && openhours.value[dayName].opens == '00:00')) {
  //       const end = new Date()
  //       end.setFullYear(end.getFullYear() + 1)
  //       const blockFromDate = new Date(from)
  //       blockFromDate.setDate(blockFromDate.getDate() + 1)
  //       dates.push({
  //         start: blockFromDate,
  //         end
  //       })
  //   }
  // }

  // /* start day price */
  // if (bookings.value.length) {
  //   const fullyBooked = []
  //   bookings.value.sort((a, b) => {
  //     const aDate = new Date(a.from)
  //     const bDate = new Date(b.from)
  //     return aDate.getTime() - bDate.getTime()
  //   })
  //   for (let i = 0; i < bookings.value.length; i++) {
  //     const booking = bookings.value[i]
  //     const from = new Date(booking.from)
  //     const to = new Date(booking.to)
  //     const dayAfterFrom = new Date(from)
  //     dayAfterFrom.setDate(dayAfterFrom.getDate() + 1)
  //     fullyBooked.push({
  //       start: dayAfterFrom,
  //       end: to
  //     })
  //   }
  //   dates.push(...fullyBooked)
  // }
  // if (maxDate.value) {
  //   dates.push({
  //     start: maxDate.value,
  //     end: null
  //   })
  // }
  // /* end day price */

  /* start hour price */
  const booking = bookings.value
    .filter((b) => !isBefore(new Date(b.from), from))
    .sort((a, b) => {
      const aDate = new Date(a.from)
      const bDate = new Date(b.from)
      return aDate.getTime() - bDate.getTime()
    })

  if (booking.length > 0) {
    const date = new Date(booking[0].from)
    date.setDate(date.getDate() + 1)
    dates.push({
      start: date,
      end: null
    })
  }
  /* end hour price */

  return dates
})
const toDateRules = ref({
  hours: (hour: number, { date }: { date: Date }) => {
    if (!date) return false
    // /* start day price */
    // // Messy, needs cleaning.
    // // Checks if there is a check in/check out on the selected day, if so, only allow hours before/after the check in/check out
    // // Also enforces a minimum 24 hour stay
    // const fromBooking = bookings.value.find((booking) => {
    //   const bookingDate = new Date(booking.from)
    //   return isSameDay(bookingDate, date)
    // })
    // const toBooking = bookings.value.find((booking) => {
    //   const bookingDate = new Date(booking.to)
    //   return isSameDay(bookingDate, date)
    // })

    // if (fromDate.value) {
    //   const minDateHours = fromDate.value.getHours()
    //   if (fromBooking) {
    //     const from = new Date(fromBooking.from)
    //     return from.getHours() >= hour && hour >= minDateHours
    //   } else if (toBooking) {
    //     const to = new Date(toBooking.to)
    //     return to.getHours() <= hour && hour >= minDateHours
    //   }
    //   return hour >= minDateHours
    // }

    // return true
    // /* end day price */

    // Block non openhours dates
    // let isOpen = true
    // const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    // if (Object.keys(openhours.value).includes(dayName)) {
    //   const opens = parseInt(openhours.value[dayName].opens.split(':')[0])
    //   let close = openhours.value[dayName].closes
    //   if (close == '23:59' || close == '00:00') {
    //     close = '24:00'
    //   }
    //   const closes = parseInt(close.split(':')[0])
    //   isOpen = hour >= opens && hour <= closes
    // }

    /* start hour price */
    const booking = bookings.value
      .filter((b) => {
        const from = new Date(b.from)
        return (
          isSameDay(from, date) &&
          from.getTime() >
            (fromDate.value ? fromDate.value : new Date()).getTime()
        )
      })
      .sort((a, b) => {
        const aDate = new Date(a.from)
        const bDate = new Date(b.from)
        return aDate.getTime() - bDate.getTime()
      })

    if (booking.length > 0) {
      const from = new Date(booking[0].from)
      if (hour > from.getHours()) {
        return false
      }
    }
    return (
      !(isToday(date) && hour < new Date().getHours()) &&
      !(
        fromDate.value &&
        isSameDay(date, fromDate.value) &&
        hour <= fromDate.value.getHours()
      ) // &&
      // isOpen
    )
    /* end hour price */
  },
  minutes: 0
})

watch(regNumber, (value) => {
  const v = value.toUpperCase()
  if (value != v) {
    regNumber.value = v
  }
})

const feeCard = convertCurrency(5, 'SEK', props.parking.currency) // SEK

const totalPrice = computed(() => {
  let price = props.parking.price
  if (toDate.value && fromDate.value) {
    //price = props.parking.price * differenceInHours(toDate.value ?? 0, fromDate.value ?? 0)
    price =
      props.parking.price *
      Math.ceil(differenceInHours(toDate.value ?? 0, fromDate.value ?? 0) / 24)
  }
  let total = price
  // if (paymentOption.value == 'card') {
  //   total += feeCard
  // }
  return {
    price,
    tax: price * 0.25,
    total
  }
})

onMounted(() => {
  if (
    route.query?.parkingId == props.parking.id &&
    route.query.checkoutId &&
    route.query.parkingBookingId
  ) {
    validatePayment()
  }
})

watch(modalOpen, (value) => {
  if (!value) {
    waitingPayment.value = false
    notificationsStore.removeNotification('location.waitingStripePayment')
  }
})
</script>

<template>
  <div>
    <TtParkingTile
      :as="'button'"
      @click="modalOpen = true"
      :parking="parking"
      :available="!booked"
      :image="locationImageSrc"
    />
    <TtContainer
      @set-open="(val) => (modalOpen = val)"
      variant="popup-card-offset"
      :open="modalOpen"
    >
      <template #header>
        <div class="relative w-full">
          <img
            :src="locationImageSrc"
            alt=""
            class="mx-auto -mt-7 block aspect-[99/88] w-36 rounded object-cover"
          />
          <button
            class="absolute right-8 top-[60px]"
            @click="modalOpen = false"
          >
            <IconClose class="h-3.5 w-3.5 text-tt-gray" />
          </button>
          <div
            aria-hidden="true"
            class="absolute -bottom-6 left-0 z-10 block w-full px-1.5 pb-4 pt-2"
          >
            <div
              class="absolute inset-0 z-0 backdrop-blur-[1.5px] [mask-image:linear-gradient(transparent,white_36%)]"
            ></div>
            <div
              class="absolute inset-0 z-0 bg-gradient-to-b from-white from-40% to-[transparent]"
            ></div>
          </div>
        </div>
      </template>
      <div class="overflow-auto px-5 pb-8 pt-4.5">
        <div class="flex items-center justify-center gap-2">
          <IconParking class="h-6 w-6" />
          <h2 class="text-3xl font-semibold tracking-tight">
            {{ parking.name }}
          </h2>
        </div>
        <div class="text-center text-sm font-light">
          Lat {{ parking.coordinates.lat }}, Long {{ parking.coordinates.lng }}
        </div>
        <p class="mt-5 text-center font-light">
          {{ t('location.bookingManagedByTT') }}
        </p>
        <div class="mt-11 grid grid-cols-2 gap-x-4 gap-y-4">
          <div class="col-span-2">
            <TtInput
              v-model="regNumber"
              id="regNumber"
              type="text"
              :dark="true"
              :maxlength="10"
              :disabled="waitingPayment"
            >
              {{ t('profile.regNumber') }}
            </TtInput>
          </div>
          <div>
            <TtDatepicker
              ref="datepickerFrom"
              v-model="fromDate"
              :is-disabled="waitingPayment"
              :disabledDates="fromDisabledDates"
              :rules="fromDateRules"
              xv-on:set-date="(value) => setStartDate(value)"
              v-on:toggle-calendars="closeDateTo"
            >
              {{ t('location.checkinLabel') }}
            </TtDatepicker>
          </div>
          <div>
            <TtDatepicker
              ref="datepickerTo"
              v-model="toDate"
              :align="'right'"
              :isDisabled="fromDate === undefined || waitingPayment"
              :disabledDates="toDisabledDates"
              :rules="toDateRules"
              xv-on:set-date="(value) => (toDate = value)"
              v-on:toggle-calendars="closeDateFrom"
            >
              {{ t('location.checkoutLabel') }}
              <span v-if="fromDate && toDate" class="font-light">
                <!--({{ differenceInHours(toDate, fromDate) }} {{ differenceInHours(toDate, fromDate) > 1 ? 'timmar' : 'timme' }})-->
                ({{ Math.ceil(differenceInHours(toDate, fromDate) / 24) }}
                {{ t('location.daysUnit') }})
              </span>
            </TtDatepicker>
          </div>
          <div class="col-span-2" v-if="hasRole('companyadmin')">
            <label class="block text-lg font-medium">{{
              t('location.guest')
            }}</label>
            <!-- TODO: Should use the TtListbox component but doesnt look ok -->
            <!-- <TtListbox
              class="relative mt-2"
              :dark="true"
              :options="truckers"
              optionDisplay="deeznuts"
              :defaultValue="selectedTrucker"
              @update="(val) => (selectedTrucker = val)"
            >
              <template #label>
                <div class="flex items-center">
                  <ProfilePicture
                    :userId="selectedTrucker.id"
                    class="h-6 w-6 rounded-full object-cover"
                  />
                  <span class="ml-2 text-lg"
                    >{{ selectedTrucker.firstname }}
                    {{ selectedTrucker.lastname }}</span
                  >
                </div>
              </template>
              <template #option="{ option }">
                <div class="flex items-center">
                  <ProfilePicture
                    :userId="option.id"
                    class="h-6 w-6 rounded-full object-cover"
                  />
                  <span class="ml-2 text-lg"
                    >{{ option.firstname }} {{ option.lastname }}</span
                  >
                </div>
              </template>
            </TtListbox> -->
            <Listbox v-model="selectedTrucker" as="div" class="relative mt-2">
              <ListboxButton
                class="form-input flex w-full items-center justify-between space-x-2 rounded border border-tt-light-gray px-3 py-3 text-base font-light transition-colors focus:border-tt-green focus:outline-none focus:ring-0"
              >
                <div class="flex items-center">
                  <ProfilePicture
                    :userId="selectedTrucker.id"
                    class="h-6 w-6 rounded-full object-cover"
                  />
                  <span class="ml-2 text-lg"
                    >{{ selectedTrucker.firstname }}
                    {{ selectedTrucker.lastname }}</span
                  >
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="6"
                  fill="none"
                  viewBox="0 0 11 6"
                >
                  <path
                    fill="#374650"
                    d="M10.313 1.063a.685.685 0 0 1-.202.486L5.986 5.674a.687.687 0 0 1-.972 0L.889 1.549A.687.687 0 1 1 1.86.576L5.5 4.216 9.14.575a.687.687 0 0 1 1.172.487Z"
                  />
                </svg>
              </ListboxButton>
              <ListboxOptions
                class="absolute z-10 mt-3 max-h-40 w-full overflow-auto rounded bg-white shadow-sidebar outline-none"
              >
                <div
                  v-for="(trucker, index) in truckers"
                  :key="trucker.id"
                  :class="`${
                    index < truckers.length - 1
                      ? 'border-b border-black/10'
                      : ''
                  }`"
                >
                  <ListboxOption
                    as="template"
                    v-slot="{ active }"
                    :key="trucker.id"
                    :value="trucker"
                  >
                    <button
                      :class="`flex w-full cursor-pointer items-center justify-between rounded px-3 py-3 text-left text-base ${
                        active && 'ring-1 ring-inset ring-tt-green'
                      }`"
                    >
                      <div class="flex items-center">
                        <ProfilePicture
                          :userId="trucker.id"
                          class="h-6 w-6 rounded-full object-cover"
                        />
                        <span class="ml-2 text-lg"
                          >{{ trucker.firstname }} {{ trucker.lastname }}</span
                        >
                      </div>
                      <span v-if="active">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="8"
                          fill="none"
                        >
                          <path
                            fill="currentColor"
                            d="M9.101.47a.47.47 0 1 1 .941 0v3.296c0 .953-.772 1.725-1.726 1.725H1.607l1.706 1.707a.47.47 0 0 1-.665.665L.139 5.353a.469.469 0 0 1 0-.665l2.51-2.512a.47.47 0 0 1 .665.665L1.607 4.548h6.71a.784.784 0 0 0 .784-.784V.47Z"
                          />
                        </svg>
                      </span>
                    </button>
                  </ListboxOption>
                </div>
              </ListboxOptions>
            </Listbox>
          </div>
        </div>
        <div class="mt-8 space-y-0.5">
          <div class="flex items-center justify-between text-lg">
            <dt class="py-2 pr-2 font-light tracking-tight">
              1 {{ t('location.dayAtRate') }} {{ parking.price }}
              {{ parking.currency }}
            </dt>
            <dd class="font-mono tracking-tighter">
              {{ totalPrice.price.toFixed(2) }} {{ parking.currency }}
            </dd>
          </div>
          <div class="flex items-center justify-between text-lg">
            <dt class="py-2 pr-2 font-light tracking-tight">
              {{ t('location.vatAmount') }}
            </dt>
            <dd class="font-mono tracking-tighter">
              {{ totalPrice.tax.toFixed(2) }} {{ parking.currency }}
            </dd>
          </div>
         <!-- <div
            v-if="paymentOption == 'card'"
            class="flex items-center justify-between text-lg"
          >
            <dt class="py-2 pr-2 font-light tracking-tight">
              Serviceavgift, 5kr
            </dt>
            <dd class="font-mono tracking-tighter">
              {{ convertCurrency(feeCard, 'SEK', parking.currency).toFixed(2) }}
              {{ parking.currency }}
            </dd>
          </div> -->
          <div
            class="mt-4 flex items-center justify-between border-t border-black/10 pt-2 text-lg"
          >
            <dt class="font-semibold tracking-tight">
              {{ t('location.totalInclVat') }}
            </dt>
            <dd class="font-mono tracking-tighter">
              {{ totalPrice.total.toFixed(2) }}
              {{ parking.currency }}
            </dd>
          </div>
        </div>
        <div class="mt-10">
          <RadioGroup v-model="paymentOption" class="grid gap-3">
            <RadioGroupLabel class="sr-only">{{
              t('location.selectPaymentMethod')
            }}</RadioGroupLabel>
            <!--<RadioGroupOption
              v-slot="{ checked }"
              class="flex h-[3.25rem] cursor-pointer items-center justify-between rounded border border-black/10 pl-4 pr-3 text-lg font-semibold tracking-tighter"
              value="swish"
            >
              <div class="flex items-center gap-4">
                <span
                  class="flex h-4.5 w-4.5 items-center justify-center rounded-full border border-tt-light-gray"
                  ><span
                    class="h-3 w-3 rounded-full bg-tt-gray"
                    v-show="checked"
                  ></span
                ></span>
                <span>Swish</span>
              </div>
              <div class="flex h-6 w-6 items-center justify-center">
                <IconSwish class="h-6 w-6" />
              </div>
            </RadioGroupOption>-->
            <RadioGroupOption
              v-if="availablePaymentOptions.includes('card')"
              v-slot="{ checked }"
              :disabled="waitingPayment"
              class="flex h-[3.25rem] cursor-pointer items-center justify-between rounded border border-black/10 pl-4 pr-3 text-lg font-semibold tracking-tighter"
              value="card"
            >
              <div class="flex items-center gap-4">
                <span
                  class="flex h-4.5 w-4.5 items-center justify-center rounded-full border border-tt-light-gray"
                >
                  <span
                    class="h-3 w-3 rounded-full bg-tt-gray"
                    v-show="checked"
                  >
                  </span>
                </span>
                <span>{{ t('location.cardPayment') }}</span>
              </div>
              <div class="flex h-6 w-6 items-center justify-center">
                <IconCard class="w-5" />
              </div>
            </RadioGroupOption>
            <RadioGroupOption
              v-if="availablePaymentOptions.includes('invoice')"
              v-slot="{ checked }"
              :disabled="waitingPayment"
              class="flex h-[3.25rem] cursor-pointer items-center justify-between rounded border border-black/10 pl-4 pr-3 text-lg font-semibold tracking-tighter"
              value="invoice"
            >
              <div class="flex items-center gap-4">
                <span
                  class="flex h-4.5 w-4.5 items-center justify-center rounded-full border border-tt-light-gray"
                >
                  <span
                    class="h-3 w-3 rounded-full bg-tt-gray"
                    v-show="checked"
                  >
                  </span>
                </span>
                <span>{{ t('location.invoiceToEmployer') }}</span>
              </div>
              <div class="flex h-6 w-6 items-center justify-center">
                <IconWallet class="w-5" />
              </div>
            </RadioGroupOption>
          </RadioGroup>
        </div>
        <div class="mt-6">
          <p class="font-light leading-[1.7] tracking-tight">
            {{ t('location.termsTextPrefix') }}
            <a
              class="border-b border-current text-tt-pure-blue"
              target="_blank"
              href="https://trucktrust.se/parkeringsvillkor/"
              >{{ t('location.termsLinkText') }}
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline"
              >
                <path
                  d="M6.11111 1.11111C5.80428 1.11111 5.55556 0.862378 5.55556 0.555556C5.55556 0.248733 5.80428 0 6.11111 0H9.44444C9.59178 0 9.73311 0.0585334 9.83728 0.162717C9.94144 0.266906 10 0.408211 10 0.555556V3.88889C10 4.19572 9.75128 4.44444 9.44444 4.44444C9.13761 4.44444 8.88889 4.19572 8.88889 3.88889V1.89679L3.72617 7.0595C3.50921 7.27644 3.15746 7.27644 2.94049 7.0595C2.72354 6.84256 2.72354 6.49078 2.94049 6.27383L8.10322 1.11111H6.11111ZM0 2.22222C0 1.60857 0.497461 1.11111 1.11111 1.11111H3.88889C4.19572 1.11111 4.44444 1.35984 4.44444 1.66667C4.44444 1.97349 4.19572 2.22222 3.88889 2.22222H1.11111V8.88889H7.77778V6.11111C7.77778 5.80428 8.0265 5.55556 8.33333 5.55556C8.64017 5.55556 8.88889 5.80428 8.88889 6.11111V8.88889C8.88889 9.50256 8.39145 10 7.77778 10H1.11111C0.497461 10 0 9.50256 0 8.88889V2.22222Z"
                  fill="currentColor"
                /></svg></a
            >.
          </p>
        </div>
        <div class="mt-2.5">
          <label class="flex items-center gap-2">
            <input
              type="checkbox"
              class="form-checkbox h-4.5 w-4.5 rounded border border-tt-light-gray bg-transparent text-tt-gray transition-colors focus:border-tt-green focus:outline-none focus:ring-0"
              v-model="acceptTerms"
              :disabled="waitingPayment"
            />
            <span class="whitespace-nowrap font-medium tracking-tight">
              {{ t('location.acceptTerms') }}
            </span>
          </label>
        </div>
        <div class="mt-14 grid gap-1.5">
          <TtButton
            @click="bookParking"
            size="large"
            type="primary"
            as="button"
            :disabled="
              !acceptTerms ||
              !fromDate ||
              !toDate ||
              waitingPayment ||
              !currentRegNumber
            "
          >
            <span class="flex gap-1">
              <span>{{
                paymentOption == 'card'
                  ? t('location.bookAndPay')
                  : t('location.bookAndInvoice')
              }}</span>
              <span class="font-normal"
                >({{ totalPrice.total.toFixed(2) }}
                {{ parking.currency }})</span
              >
            </span>
          </TtButton>
        </div>
      </div>
    </TtContainer>
    <ParkingBookingConfirmation
      :location="location"
      :parking="parking"
      :booking="bookingObjectForConfirmation"
      :open="confirmationModalOpen"
      @close="confirmationModalOpen = false"
    />
  </div>
</template>
